import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "../../../atoms/Spinner";
import LoginModal from "./LoginModal";
import PageNotWorking from "./PageNotWorking";
import { CustomButton } from "../../../atoms/CustomButton";
import {
  useGetProcessData,
  useLoginEntityUser,
  useLoginPageSocialLogin,
} from "../../../../hooks/entity";
import { useUserForgotPassword } from "../../../../hooks/users";
import { useLoginPagePassLessValidation } from "../../../../hooks/auth/passwordless";
import isSafeRedirectUrl from "../../../../utils/safeRedirect";
import { useSearchWithSecrets } from "../../../../utils/addressSecrets";
import PassLessLoginModal from "../../auth/PassLessLoginModal";
import { loginErrorKey } from "./loginErrors";
import { usePasswordResetToasts } from "../../../../hooks/usePasswordResetToasts";

const INVALID_LINK = "loginPage.errors.invalidLink";

const LoginViaLink = ({ externalPage = false }: { externalPage?: boolean }) => {
  const { t } = useTranslation("clientView");
  const { login_code } = useParams();
  const redirect = useSearchWithSecrets().get("redirect");
  const [entity_data, setEntity_data] = useState<any>();
  const [showModal, setShowModal] = useState(false);
  const [showPassLess, setShowPassLess] = useState(false);
  const [passLessEmail, setPassLessEmail] = useState("");

  const { isLoading, isError, isSuccess, refetch } = useGetProcessData(
    login_code as string,
    {
      onSuccess(data) {
        setEntity_data({ ...data?.entity_data });
      },
    }
  );
  const { mutate: loginUser } = useLoginEntityUser();
  const { mutate: socialLogin } = useLoginPageSocialLogin();
  const { mutate: forgotPassword, isLoading: isForgotPasswordLoading } =
    useUserForgotPassword({ notify: false });
  const resetToasts = usePasswordResetToasts();
  const { mutateAsync: validateLoginPagePassLess } =
    useLoginPagePassLessValidation();

  const showLoginError = (error: unknown) => {
    const key = loginErrorKey(error);
    toast.error(t(key) as string, { toastId: key });
  };

  const goToCallback = (element: any) => {
    const callback = element?.login_form_callback;
    if (isSafeRedirectUrl(callback)) {
      window.location.replace(callback);
      return;
    }
    setShowPassLess(false);
    toast.error(t(INVALID_LINK) as string, { toastId: INVALID_LINK });
  };

  useEffect(() => {
    if (isSuccess) setShowModal(true);
  }, [isSuccess]);

  const handleLoginViaLink = (loginData: any) => {
    const credentials: any = {
      user_email: loginData?.user_email,
      user_password: loginData?.user_password,
    };
    loginUser(
      {
        entity_identification: entity_data?.entity_identification,
        data: credentials,
        ...(redirect ? { redirect } : {}),
      },
      {
        onSuccess: (res) => goToCallback(res?.data?.element),
        onError: showLoginError,
      }
    );
  };

  const handleForgotPassword = (email: string, onSent: () => void) => {
    forgotPassword(
      {
        entityIdentification: entity_data?.entity_identification,
        user_email: email,
      },
      {
        onSuccess: () => {
          resetToasts.sent();
          onSent();
        },
        onError: resetToasts.refused,
      }
    );
  };

  const handleSocialLogin = (provider: string, data: any) => {
    const providerPayload = {
      access_token:
        provider === "google"
          ? data?.access_token
          : data?.authorization?.id_token,
      provider,
    };

    socialLogin(
      {
        entity_identification: entity_data?.entity_identification,
        data: { type: "login" as const, providerPayload },
        ...(redirect ? { redirect } : {}),
      },
      {
        onSuccess: (res) => goToCallback(res?.data?.element),
        onError: showLoginError,
      }
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center gap-2">
        {t("dashboard.features.loginTemplate.LoginViaLink.loading")}
        <span translate="no">
          <Spinner size="sm" />
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mb-5 flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
        <span>{t("dashboard.features.loginTemplate.LoginViaLink.text1")}</span>
        <CustomButton
          onClick={() => refetch()}
          className="ms-1"
          title={t("dashboard.features.loginTemplate.LoginViaLink.text2")}
        />
      </div>
    );
  }

  if (entity_data?.entity_status === "SERVICE_OFF") {
    return <PageNotWorking />;
  }

  return (
    <div className="w-full min-h-screen py-3 px-2 bg-gray-500 bg-opacity-75 flex justify-center items-start loginViaLink">
      {isSuccess && (
        <LoginModal
          showModal={showModal}
          serviceData={{
            name: entity_data?.entity_name,
            logoUrl: entity_data?.entity_logo,
          }}
          isRealLogin={true}
          handleLoginViaLink={handleLoginViaLink}
          handleSocialLogin={handleSocialLogin}
          handleForgotPassword={handleForgotPassword}
          handlePassLessLogin={(email) => {
            setPassLessEmail(email);
            setShowPassLess(true);
          }}
          isForgotPasswordLoading={isForgotPasswordLoading}
          externalPage={externalPage}
        />
      )}
      {isSuccess && entity_data?.entity_identification && (
        <PassLessLoginModal
          show={showPassLess}
          onClose={() => setShowPassLess(false)}
          defaultEmail={passLessEmail}
          entityIdentification={entity_data.entity_identification}
          namespace="clientView"
          completeWithServerToken={async (target) => {
            const res = await validateLoginPagePassLess({
              ...target,
              redirect,
            });
            return res?.data?.element;
          }}
          onAuthenticated={(element) => goToCallback(element)}
        />
      )}
    </div>
  );
};

export default LoginViaLink;
